import { CommonParams } from '@/api/resources/common/shared/models/common.models'
import { configurationAccountingBillableUnitsService } from '@/modules/configurationModule/detail/accounting/billable/units/services/configuration-accounting-billable-unit/configuration-accounting-billable-unit.service'
import { ConfigurationLocalAccountingRatesModalBasicFormState, initialLocalAccountingRatesModalBasicFormState } from '@/modules/configurationModule/detail/local-accounting/billable/rates/components/rates-modal-basic-form/configuration-local-accounting-billable-rate-modal-basic-form.model'
import { BillableRateCreateRequest, BillableRatePatchInBatchRequest, BillableRateResponse } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/configuration-local-accounting-billable-rates/billable-rate.dto'
import { billableRateService } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/configuration-local-accounting-billable-rates/billable-rate.service'
import { EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { StrictNullable } from '@/shared/types/app.types'
import { Vue, Component, Prop, Emit } from 'vue-property-decorator'
import { DateUtils } from '@/shared/util/date-utils'
import { billableRateBillableConceptService } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/billable-rate-billable-concept/billable-rate-billable-concept.service'
import { store } from '@/store/store'
import { ModalUtils } from '@/shared/util/modal-utils'
import { GridUtils } from '@/shared/util/grid-utils'

@Component
export default class ConfigurationLocalAccountingBillableRateModalBasicForm extends Vue {
    @Prop(String) readonly actionTitle!: string
    @Prop() readonly rate!: StrictNullable<BillableRateResponse>

    @Emit()
    create(createdRate: BillableRateResponse): BillableRateResponse {
        return createdRate
    }

    @Emit()
    update(updateRate: BillableRateResponse): BillableRateResponse {
        return updateRate
    }

    state: ConfigurationLocalAccountingRatesModalBasicFormState
    constructor() {
        super()
        this.state = initialLocalAccountingRatesModalBasicFormState()
    }

    async mounted() {
        await this._getUnits()
        if (this.rate) {
            const hasCopaymentType = this.state.invoiceToList.find((invoiceItem) => invoiceItem.id === this.rate?.copaymentType?.code)
            this.state = {
                ...this.state,
                rate: {
                    ...this.state.rate,
                    accountingUnit: this.rate.accountingUnit,
                    code: this.rate.code,
                    endDate: new Date(this.rate.endDate),
                    startDate: new Date(this.rate.startDate),
                    id: this.rate.id,
                    isDefault: this.rate.isDefault,
                    allowsCopayment: this.rate.allowsCopayment,
                    copaymentType: hasCopaymentType ?? this.state.rate.copaymentType,
                    name: this.rate.name
                }
            }
        }
    }

    get modelValidated(): boolean {
        const rateExists = !!this.state.rate.name.length
        const codeExists = !!this.state.rate.code.length
        const unitExists = !!this.state.rate.accountingUnit
        const startDateExists = !!this.state.rate.startDate
        const endDateExists = !!this.state.rate.endDate
        const hasErrors: boolean = this.$validator.errors.any() || !rateExists || !codeExists || !unitExists || !startDateExists || !endDateExists
        const isDirty: boolean = this.$_someFieldIsDirty(this.fields)
        return !hasErrors && isDirty
    }

    get isUpdatedForm(): boolean {
        return !!this.rate?.id
    }

    get dateToday(): Date {
        return new Date()
    }

    get dateStartCanUpdate(): boolean {
        if (this.isUpdatedForm) {
            if (this.state.rate?.startDate) {
                return this.state.rate?.startDate?.setHours(0, 0, 0, 0) >= this.state.todayDate.setHours(0, 0, 0, 0)
            }
            return false
        } else {
            return true
        }
    }

    get beforeDateStartDate(): Date {
        return this.state.todayDate
    }

    get afterDateStartDate(): StrictNullable<Date> {
        if (!this.state.rate.endDate) {
            return null
        }
        return this.state.rate.endDate || this.state.finishDate
    }

    get beforeDateEndDate(): Date {
        if (this.state.rate?.startDate) {
            return this.state.rate.startDate.setHours(0, 0, 0, 0) > this.state.todayDate.setHours(0, 0, 0, 0)
                ? this.state.rate.startDate : this.state.todayDate
        }
        return this.state.todayDate
    }

    onStartDate(value: Date): void {
        this.state.rate.startDate = value
        this._setDirtyStartDate()
    }

    onEndDate(value: Date): void {
        this.state.rate.endDate = value
        this._setDirtyEndDate()
    }

    onClearStartDate(): void {
        this.$nextTick(() => {
            this.state.rate.startDate = this.state.todayDate
            this._setDirtyStartDate(false)
        })
    }

    onClearEndDate(): void {
        this.state.rate.endDate = null
        this._setDirtyEndDate(false)
    }

    async doAction(): Promise<void> {
        if (this.isUpdatedForm) {
            if (!this.state.rate.allowsCopayment) {
                const params: CommonParams = {
                    size: PAGINATOR_BIG_PAGE_SIZE
                }

                const filterModel = {
                    'hasCopayments': {
                        filterType: 'boolean',
                        type: 'equals',
                        filter: true
                    },
                    'rate.id': GridUtils.getNumberFilterModel('equals', this.state.rate.id)
                }

                const response = await this.$_toCallSecure(billableRateBillableConceptService.paginator(filterModel, params, store.state.labId))

                if (response?.content.length) {
                    ModalUtils.showErrorModal(this.$tc('configuration.local.rates.error.checkCopaymentsConcepts'))
                    this.state.rate.allowsCopayment = true
                    return
                }
            }

            const rateUpdateOperation = await this._updateOperation()
            if (rateUpdateOperation) {
                const updatedRate: BillableRateResponse = {
                    id: this.state.rate.id!,
                    name: this.state.rate.name,
                    code: this.state.rate.code,
                    startDate: DateUtils.formatDate(this.state.rate.startDate)!,
                    endDate: DateUtils.formatDate(this.state.rate.endDate)!,
                    isDefault: this.state.rate.isDefault,
                    allowsCopayment: this.state.rate.allowsCopayment,
                    copaymentType: this.state.rate.allowsCopayment
                        ? { code: this.state.rate.copaymentType?.id!, translatedName: this.state.rate.copaymentType?.label! } : null,
                    laboratory: this.rate?.laboratory!,
                    accountingUnit: this.state.rate.accountingUnit!
                }

                // emits an event
                this.update(updatedRate)
                await this.$validator.reset()
            }
        } else {
            const rateCreateOperation = await this._createOperation()
            if (rateCreateOperation) {
                this.state.rate.id = rateCreateOperation.id
                // emits an event
                this.create(rateCreateOperation)
                await this.$validator.reset()
            }
        }
    }

    /// SERVICES CALLS ///
    private async _getUnits(): Promise<void> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const filterModel = {
            'active': {
                filterType: 'boolean',
                type: 'equals',
                filter: true
            }
        }
        const response = await this.$_toCallSecure(configurationAccountingBillableUnitsService.paginator(filterModel, params))
        this.state.units = response ? response.content : []
    }

    private _setDirtyStartDate(value:boolean = true): void {
        this.fields[this.$tc('configuration.grid.startDate')].dirty = value
    }

    private _setDirtyEndDate(value:boolean = true): void {
        this.fields[this.$tc('configuration.grid.endDate')].dirty = value
    }

    private async _addRate(
        request: BillableRateCreateRequest
    ): Promise<StrictNullable<BillableRateResponse>> {
        if (request) {
            const response = await this.$_toCallSecure(billableRateService.create(
                this.state.laboratoryId,
                request
            ))
            if (response) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
                return response
            }
        }
        return null
    }

    private async _updateRate(
        request: BillableRatePatchInBatchRequest
    ): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        if (request) {
            const response = await this.$_toCallSecure(billableRateService.patch(
                this.state.laboratoryId,
                [request]
            ))
            if (response) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
                return response
            }
        }
        return null
    }

    /// FORM OPERATIONS ///
    private _createOperation(): Promise<StrictNullable<BillableRateResponse>> {
        return this._addRate(
            {
                name: this.state.rate.name,
                code: this.state.rate.code,
                accountingUnitId: this.state.rate.accountingUnit?.id!,
                startDate: DateUtils.formatDate(this.state.rate.startDate)!,
                endDate: DateUtils.formatDate(this.state.rate.endDate)!,
                isDefault: this.state.rate.isDefault,
                allowsCopayment: this.state.rate.allowsCopayment,
                copaymentType: this.state.rate.allowsCopayment ? this.state.rate.copaymentType?.id : null
            }
        )
    }

    private _updateOperation(): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        return this._updateRate(
            {
                id: this.state.rate.id!,
                name: this.state.rate.name,
                code: this.state.rate.code,
                startDate: DateUtils.formatDate(this.state.rate.startDate)!,
                endDate: DateUtils.formatDate(this.state.rate.endDate)!,
                isDefault: this.state.rate.isDefault,
                allowsCopayment: this.state.rate.allowsCopayment,
                copaymentType: this.state.rate.allowsCopayment ? this.state.rate.copaymentType?.id : null
            }
        )
    }
}
